import { ref } from 'vue';
import http from '@monolith/legacy/services/api/http';
import Analytics from '@monolith/legacy/services/analytics';
import SignUpEvent from '@monolith/legacy/services/analytics/events/sign-up/sign-up-event';
import { SignUpEventNames } from '@monolith/legacy/services/analytics/events/sign-up/common/event-names';
import { redirectToRegistrationFunnel } from '@monolith/legacy/services/signup/registration-funnel-url';
import type { SignUpProps } from '@monolith/legacy/services/analytics/properties/sign-up-property';
import { sameOriginPath } from '@monolith/core/utilities/same-origin-path';
import { captureException } from '@core/plugins/sentry';

type GsiApi = {
  accounts: {
    id: {
      initialize: (config: unknown) => void;
      renderButton: (el: HTMLElement, config: unknown) => void;
    };
  };
};

/**
 * Takes a getter, not a snapshot: the popin can switch funnel while this form
 * stays mounted (change-audience), so the audience has to be read at click time.
 * Required, because it decides both what the events report and which funnel a
 * Google sign-up with no account is redirected into.
 */
export function useGoogleSignIn(
  getAnalyticProps: () => SignUpProps,
  getContainer: () => HTMLElement | null = () => document.getElementById('google-signin-hidden')
) {
  const googleLoading = ref(false);

  function loadGoogleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.getElementById('google-gsi-script')) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google script'));
      document.head.appendChild(script);
    });
  }

  /**
   * The funnel context as of right now, with the surface defaulted to the auth popin's
   * values — every host but the brand landing renders this flow inside that popin.
   */
  function eventProps(): SignUpProps {
    const analyticProps = getAnalyticProps();
    return {
      ...analyticProps,
      modal_variant: analyticProps.modal_variant ?? 'legacy_login_modal',
      has_usp_block: analyticProps.has_usp_block ?? false,
    };
  }

  async function onGoogleSignIn(): Promise<void> {
    googleLoading.value = true;

    Analytics.track(
      new SignUpEvent(SignUpEventNames.GoogleSignInClickedEvent, {
        type: 'Click',
        ...eventProps(),
      })
    );

    try {
      await loadGoogleScript();

      const gsi = (window as unknown as Record<string, unknown>).google as GsiApi;

      gsi.accounts.id.initialize({
        client_id: document.querySelector<HTMLMetaElement>('meta[name="google-auth"]')?.content ?? '',
        callback: handleGoogleCredentialResponse,
      });

      // Clicking Google's own hidden button is what opens the account chooser, and the
      // spinner is only released again by handleGoogleCredentialResponse. So if there is
      // no container, or GSI rendered nothing into it, nothing will ever be clicked and
      // the button has to be re-enabled here — otherwise it stays dead for good.
      const container = getContainer();
      if (!container) {
        googleLoading.value = false;
        return;
      }

      container.innerHTML = '';
      gsi.accounts.id.renderButton(container, { type: 'icon', size: 'large' });

      const button = container.querySelector<HTMLElement>('div[role="button"]');
      if (!button) {
        googleLoading.value = false;
        return;
      }

      button.click();
    } catch {
      googleLoading.value = false;
    }
  }

  async function handleGoogleCredentialResponse(response: { credential: string }): Promise<void> {
    try {
      const result = await http().post('/auth/login-google', {
        id_token: response.credential,
      });

      if (result.data.action === 'login') {
        Analytics.track(
          new SignUpEvent(SignUpEventNames.GoogleSignInLoginEvent, {
            type: 'Google Sign In',
            ...eventProps(),
          })
        );

        // `previous` is user-supplied, so it must never be able to send them off-site. A
        // target that cannot be reduced to a path falls through to the default below.
        const previousPath = sameOriginPath(new URLSearchParams(window.location.search).get('previous'));
        if (previousPath) {
          window.location.href = previousPath;
          return;
        }

        window.location.href = '/';
      } else if (result.data.action === 'register') {
        const profile = result.data.profile;
        Analytics.track(
          new SignUpEvent(SignUpEventNames.GoogleSignInRegisterEvent, {
            type: 'Google Sign In',
            ...eventProps(),
          })
        );
        // Same shared funnel builder as the email path: it diverges by audience
        // (brand -> /brand-registration, retailer -> /register) and carries the
        // already-verified token minted by platform-auth-service (PR2) so the
        // register form skips the email-code step for Google sign-ups.
        redirectToRegistrationFunnel({
          audience: getAnalyticProps().audience,
          email: profile.email,
          verificationToken: result.data.email_verification_token,
          google: { firstName: profile.first_name, lastName: profile.last_name, id: profile.google_id },
        });
      }
    } catch (err) {
      captureException(`Google sign-in exchange failed: ${err}`);
    } finally {
      googleLoading.value = false;
    }
  }

  return { googleLoading, onGoogleSignIn, handleGoogleCredentialResponse };
}
